import { stepFailedArea, stepLabel } from './stepLabels';

/**
 * What the panel shows of a chat with the platform agent (docs/platform-agent/contracts.md §5), built from eve's
 * conversation state (`useEveAgent().data`): the person's messages, the agent's answers (Markdown), each tool call
 * as a short step, its questions, and how a turn ended (stopped or failed). Pure, so it is tested without eve.
 */

/**
 * The step list's labels (D3): the translation key of each tool's step and the input field its label names, or null
 * for a call that isn't shown. Keys live in Components:PlatformAgent:PlatformAgentConversation `steps`. The working
 * line names the step being taken by contracts §5's table instead (stepLabels.js, platformAgentCurrentStep).
 */
export const PLATFORM_AGENT_STEPS = Object.freeze({
  foodamigos__areas: { key: 'areas' },
  foodamigos__area_describe: { key: 'area_describe', field: 'area' },
  foodamigos__read: { key: 'read' },
  foodamigos__propose: { key: 'propose' },
  foodamigos__change_set_get: { key: 'change_set_get' },
  foodamigos__gap_record: { key: 'gap_record' },
  foodamigos__request_log: null,
  research: { key: 'research' },
  screen_find: { key: 'screen_find' },
  file_read: { key: 'file_read' },
  shop_check: { key: 'shop_check' },
  show_card: null,
  // Amigo noting a refusal before answering it (agent/tools/refusal_note.ts): the answer says it, not a step.
  refusal_note: null,
  // eve's own: loading a skill, searching its skills and tools and reading a skill's file are the agent finding its
  // way, not work for the person (each showed as "Checking something"), and a question shows as its card.
  eve__skill: null,
  eve__search: null,
  eve__tool: null,
  read_file: null,
  ask_question: null,
});

/**
 * The areas of contracts §0, each with its name in the person's language (`areas` in the same catalog). The agent
 * names an area by this slug, which is never shown as it is.
 */
export const PLATFORM_AGENT_AREAS = Object.freeze([
  'menu',
  'hours',
  'order_settings',
  'my_info',
  'marketing',
  'reports',
]);

/**
 * The step's label: the §5 table's, with the area it names in the person's language. A call the table doesn't know
 * (a tool added to the agent before the panel) gets a plain step, never its tool name, and an area the panel doesn't
 * know yet is "this area". Null when the call isn't shown.
 */
export function platformAgentStepLabel(part, t) {
  const entry = PLATFORM_AGENT_STEPS[part.toolName];

  if (entry === null) {
    return null;
  }

  if (!entry) {
    return t('steps.other');
  }

  if (entry.field !== 'area') {
    return t(`steps.${entry.key}`);
  }

  const area = part.input?.area;

  return PLATFORM_AGENT_AREAS.includes(area)
    ? t(`steps.${entry.key}`, { area: t(`areas.${area}`) })
    : t(`steps.${entry.key}_other`);
}

/**
 * What a step that failed reads as in the step list (QL46, contracts §5 Failed steps): what couldn't be done, for the
 * area it worked in (`previous`: the turn's earlier calls), from the catalog's `failed_steps`, so it reads in the
 * person's language like every other step. null when a failed step isn't shown; undefined for a tool the table
 * doesn't know, which keeps its own label.
 */
export function platformAgentFailedStepLabel(part, t, previous = []) {
  const area = stepFailedArea(part.toolName, part.input, previous);
  const key = PLATFORM_AGENT_STEPS[part.toolName]?.key;

  if (area === null) {
    return null;
  }

  if (area === undefined || !key) {
    return undefined;
  }

  return t(`failed_steps.${key}.${area === '*' ? 'other' : area}`);
}

/**
 * A call's step state. One still running when its turn stopped, failed or was cut off (QA81: the stream broke, the
 * agent became unavailable, the chat ended) is `stopped`; one a turn that completed left unanswered isn't shown.
 */
function stepState(part, turnActive, turnInterrupted = false) {
  switch (part.state) {
    case 'output-available':
      return part.partial && turnActive ? 'running' : 'done';
    case 'output-error':
      return 'failed';
    case 'output-denied':
      return 'denied';
    default:
      if (turnActive) {
        return 'running';
      }

      return turnInterrupted ? 'stopped' : null;
  }
}

function messageText(message) {
  return message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('\n')
    .trim();
}

function isTurnActive(conversation, message) {
  const turn = message.metadata?.turnId ? conversation.turns?.[message.metadata.turnId] : undefined;

  if (turn) {
    return turn.status === 'active';
  }

  return message.metadata?.status === 'streaming';
}

/** A turn that ended without its answer: stopped or failed. */
function isTurnInterrupted(conversation, message) {
  const status = message.metadata?.turnId ? conversation.turns?.[message.metadata.turnId]?.status : undefined;

  return status === 'cancelled' || status === 'failed';
}

/** A turn that ended with its answer: not one still running, stopped or failed. */
function isTurnFinished(conversation, message) {
  const turn = message.metadata?.turnId ? conversation.turns?.[message.metadata.turnId] : undefined;

  if (turn) {
    return turn.status === 'completed';
  }

  return message.metadata?.status === 'complete';
}

/** The cards the panel draws (§5 show_card); a kind it doesn't know yet shows nothing. */
export const PLATFORM_AGENT_CARD_KINDS = Object.freeze(['change_set', 'shop_screenshot']);

/** The card a finished show_card call shows: its result, which is the card the agent passed. */
function shownCard(part) {
  if (part.state !== 'output-available') {
    return null;
  }

  const card = part.output?.kind ? part.output : part.input?.card;

  if (card?.kind === 'shop_screenshot' && typeof card.pathname !== 'string') {
    return null;
  }

  return PLATFORM_AGENT_CARD_KINDS.includes(card?.kind) ? card : null;
}

/**
 * The model's working notes (QA33): text it writes to itself between calls, such as "Truncated; read next pages and
 * the products.". Only a turn's text after its last call is the answer, so a text part that another call of the same
 * turn follows, in that message or a later one, is a note. A card isn't such a call: what the agent says before its
 * card (what it couldn't read in a file) stays. A question starts over: what it says before asking stays, and only a
 * call after the answer makes later text a note. While the turn runs, its text after the last call so far shows (the
 * answer streaming in) until a call follows it. A completed turn without text after its last call (it answered, then
 * noted the request with gap_record) keeps its last text before that call as its answer, so a turn never ends without
 * one. Returns the notes' parts.
 */
function workingNotes(conversation) {
  const notes = new Set();
  // By turn: the text since its last call, the text that call made notes, and whether the turn completed.
  const turns = new Map();

  (conversation?.messages ?? []).forEach((message) => {
    if (message.role === 'user') {
      return;
    }

    const id = message.metadata?.turnId ?? message.id;
    const turn = turns.get(id) ?? { since: [], last: [] };
    turn.completed = isTurnFinished(conversation, message);
    turns.set(id, turn);

    message.parts.forEach((part) => {
      if (part.type === 'text' && part.text.trim()) {
        turn.since.push(part);
      } else if (part.type === 'dynamic-tool' && part.toolName !== 'show_card') {
        if (part.toolName !== 'ask_question' && turn.since.length > 0) {
          turn.since.forEach((note) => notes.add(note));
          turn.last = turn.since;
        } else if (part.toolName === 'ask_question') {
          turn.last = [];
        }

        turn.since = [];
      }
    });
  });

  turns.forEach((turn) => {
    if (turn.completed && turn.since.length === 0) {
      turn.last.forEach((answer) => notes.delete(answer));
    }
  });

  return notes;
}

/**
 * An agent message's text, steps and cards, in order, from its part `from` up to `to`; consecutive steps form one
 * list. `notes` aren't shown. A step its turn left running when it was `interrupted` ends without a spinner, "Stopped"
 * (QA81): the DS AgentSteps' pending look with the word as its tag (the conversation reads pending out as "Stopped"
 * too). A step that failed says what couldn't be done (QL46, §5 Failed steps), or isn't shown when the table says so.
 * `calls` are the turn's calls so far, from its earlier messages and pieces too, and this piece's are added: a failed
 * read names the area of the turn's latest area_describe, even one in an earlier message of the turn.
 */
function agentItems(
  message,
  active,
  t,
  notes,
  { from = 0, to = message.parts.length, interrupted = false, calls = [] } = {},
) {
  const items = [];

  message.parts.forEach((part, index) => {
    if (index < from || index >= to) {
      return;
    }

    if (part.type === 'text' && part.text.trim()) {
      if (!notes.has(part)) {
        items.push({ kind: 'text', id: part.id ?? `${message.id}:${index}`, text: part.text });
      }

      return;
    }

    if (part.type !== 'dynamic-tool') {
      return;
    }

    const previous = [...calls];
    calls.push({ tool: part.toolName, input: part.input });
    const card = part.toolName === 'show_card' ? shownCard(part) : null;

    if (card) {
      items.push({ kind: 'card', id: part.toolCallId, card });

      return;
    }

    const shownLabel = platformAgentStepLabel(part, t);
    const state = shownLabel ? stepState(part, active, interrupted) : null;

    if (!state) {
      return;
    }

    const failedLabel = state === 'failed' ? platformAgentFailedStepLabel(part, t, previous) : undefined;

    if (failedLabel === null) {
      return;
    }

    const label = failedLabel ?? shownLabel;

    const step =
      state === 'stopped'
        ? { id: part.toolCallId, label, state: 'pending', tag: t('step_stopped') }
        : { id: part.toolCallId, label, state };
    const last = items.at(-1);

    if (last?.kind === 'steps' && last.steps.at(-1).label === label && last.steps.at(-1).state === 'done') {
      // The same step again after it was done (twenty reads of the orders, QA6): one line, as far as the last of them
      // got. One that failed or was refused keeps its own line, so the list still says so.
      last.steps[last.steps.length - 1] = step;
    } else if (last?.kind === 'steps') {
      last.steps.push(step);
    } else {
      items.push({ kind: 'steps', id: `${message.id}:steps:${index}`, steps: [step] });
    }
  });

  return items;
}

function questionBlock(input) {
  const { request, response, status } = input;
  const options = request.options ?? [];
  const chosen = response?.optionId ? options.find((option) => option.id === response.optionId) : undefined;
  const answer = chosen?.label ?? response?.text ?? null;

  return {
    kind: 'question',
    id: `question:${request.requestId}`,
    requestId: request.requestId,
    callId: request.action?.callId ?? null,
    turnId: input.turnId,
    prompt: request.prompt,
    options: options.map(({ id, label, description }) => ({ id, label, description })),
    freeform: request.allowFreeform === true || options.length === 0,
    open: status === 'open',
    answer: status === 'open' ? null : answer,
  };
}

/**
 * An agent message cut where it asked back (QA35): the parts before each question it asked (`asked`, the question
 * blocks by the call that asked them), then the question, and so on; the last piece has no question. The question
 * keeps its place in the turn, before the steps, cards and text that came after the answer.
 */
function piecesAtQuestions(message, asked) {
  const pieces = [];
  let from = 0;

  message.parts.forEach((part, index) => {
    const question = part.type === 'dynamic-tool' ? asked.get(part.toolCallId) : undefined;

    if (question) {
      pieces.push({ from, to: index, question });
      from = index + 1;
    }
  });

  pieces.push({ from, to: message.parts.length, question: null });

  return pieces;
}

/** Puts each extra block after the last block of its turn, or at the end when that turn shows nothing yet. */
function placeByTurn(blocks, extras) {
  const placed = [...blocks];

  extras.forEach((extra) => {
    let at = -1;

    placed.forEach((block, index) => {
      if (extra.turnId && block.turnId === extra.turnId) {
        at = index;
      }
    });

    placed.splice(at === -1 ? placed.length : at + 1, 0, extra);
  });

  return placed;
}

/**
 * The files a message of the person's carried (§8), from `files` (usePlatformAgentChat): by eve's id of the message
 * once eve has it, else (eve's own bubble until the agent confirms it, or one that failed) the first files sent with
 * that text not shown yet.
 */
function userUploads(message, text, files, shown) {
  const known = files.byMessage?.[message.id];

  if (known) {
    return known;
  }

  if (!message.metadata?.optimistic && message.metadata?.status !== 'failed') {
    return [];
  }

  const entry = (files.pending ?? []).find((candidate) => candidate.text === text && !shown.has(candidate));

  if (!entry) {
    return [];
  }

  shown.add(entry);

  return entry.uploads;
}

/**
 * The chat as blocks, oldest first:
 * - `user` { text, failed, uploads }: a message the person sent (failed: it never reached the agent), with the files
 *   it carried (§8 Upload; `files` is usePlatformAgentChat's `{ byMessage, pending }`).
 * - `agent` { items: [text | steps | card], messageId, active, finished, continued }: the agent's turn, cut where it
 *   asked back (`messageId` is eve's message). `finished` once the turn completed, on its last block (Helpful? asks
 *   about its answer). `continued` when the block before is the agent's too, so only the first shows the avatar and
 *   name. Its text is the answer, never the working notes between its calls (`workingNotes`).
 * - `question` { requestId, prompt, options, freeform, open, answer }: the agent asked back (eve input request), where
 *   it asked, also once answered.
 * - `stopped` / `failed` { turnId }: how a turn ended, after its last block.
 *
 * `interrupted` (QA81): the chat can't go on with what is running, as the stream broke, the agent is unavailable or the
 * chat ended, so no turn is active any more and the steps it left running show as stopped.
 */
export function platformAgentBlocks(conversation, t, files = {}, { interrupted = false } = {}) {
  const blocks = [];
  const shownPending = new Set();
  const messages = conversation?.messages ?? [];
  const notes = workingNotes(conversation);
  // Each turn's calls so far, by turn: a turn can run over several of the agent's messages.
  const callsByTurn = new Map();
  const questions = Object.values(conversation?.inputs ?? {})
    .filter((input) => input.request?.kind === 'question')
    .map(questionBlock);
  // The questions by the call that asked them: each goes where its message asked it; one whose call the messages
  // don't show (yet) goes after its turn.
  const asked = new Map(questions.filter((question) => question.callId).map((question) => [question.callId, question]));
  const placed = new Set();

  messages.forEach((message) => {
    const turnId = message.metadata?.turnId ?? null;

    if (message.role === 'user') {
      const text = messageText(message);

      if (text) {
        blocks.push({
          kind: 'user',
          id: message.id,
          turnId,
          text,
          failed: message.metadata?.status === 'failed',
          uploads: userUploads(message, text, files, shownPending),
        });
      }

      return;
    }

    const active = isTurnActive(conversation, message) && !interrupted;
    const stopped = interrupted || isTurnInterrupted(conversation, message);
    const pieces = piecesAtQuestions(message, asked);
    const turnCalls = callsByTurn.get(turnId ?? message.id) ?? [];
    callsByTurn.set(turnId ?? message.id, turnCalls);

    pieces.forEach(({ from, to, question }, index) => {
      const items = agentItems(message, active, t, notes, { from, to, interrupted: stopped, calls: turnCalls });
      const last = index === pieces.length - 1;

      if (items.length > 0) {
        blocks.push({
          kind: 'agent',
          id: index === 0 ? message.id : `${message.id}:after:${pieces[index - 1].question.requestId}`,
          messageId: message.id,
          turnId,
          items,
          active,
          finished: last && isTurnFinished(conversation, message),
          continued: blocks.at(-1)?.kind === 'agent',
        });
      }

      if (question) {
        blocks.push(question);
        placed.add(question);
      }
    });
  });

  const endings = Object.values(conversation?.turns ?? {})
    .filter((turn) => turn.status === 'cancelled' || turn.status === 'failed')
    .map((turn) => ({
      kind: turn.status === 'cancelled' ? 'stopped' : 'failed',
      id: `${turn.status}:${turn.turnId}`,
      turnId: turn.turnId,
    }));

  const unplaced = questions.filter((question) => !placed.has(question));

  return placeByTurn(placeByTurn(blocks, unplaced), endings);
}

/**
 * The chat's notices (contracts §6 Notice: what Amigo posted on its own, like an import's result) among its blocks, as
 * Amigo's messages where they happened (QA51): after the last block of a turn that began before the notice, so the
 * newest message is the newest state. A turn begun after it, and a message not sent yet, come after it; a chat whose
 * turns have no time yet gets them at its end. Each notice block is `notice` { notice } with its own `turnId`, whose
 * time `times` (turn id → when it began, usePlatformAgentChat) gains, so the day lines date it.
 */
export function withPlatformAgentNotices(blocks, times, notices) {
  const list = (Array.isArray(notices) ? notices : [])
    .filter((notice) => notice?.id && !Number.isNaN(Date.parse(notice.at)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

  if (list.length === 0) {
    return { blocks, times };
  }

  const placed = [...blocks];
  const dated = { ...times };
  const began = (block) => Date.parse(block.turnId ? dated[block.turnId] : undefined);

  list.forEach((notice) => {
    const at = Date.parse(notice.at);
    const turnId = `notice:${notice.id}`;
    let after = -1;
    let timed = false;

    placed.forEach((block, index) => {
      const time = began(block);

      if (!Number.isNaN(time)) {
        timed = true;

        if (time <= at) {
          after = index;
        }
      }
    });

    dated[turnId] = notice.at;
    placed.splice(timed ? after + 1 : placed.length, 0, { kind: 'notice', id: turnId, turnId, notice });
  });

  return { blocks: placed, times: dated };
}

/** The question the person can answer by typing in the message box: the last open one that takes free text. */
export function openFreeformQuestion(blocks) {
  return blocks.findLast((block) => block.kind === 'question' && block.open && block.freeform) ?? null;
}

/**
 * Whether a question waits for the person's answer (QA73): the turn is paused on it, not working, so the panel shows
 * neither its working line nor Stop.
 */
export function waitsForAnswer(blocks) {
  return blocks.some((block) => block.kind === 'question' && block.open);
}

/**
 * Whether the panel shows its working line while a turn runs: from the message (design: within 400 ms of sending)
 * through every step, until the answer or a card replaces it. Not while a question waits for the person.
 */
export function showsWorkingLine(blocks, working) {
  if (!working || waitsForAnswer(blocks)) {
    return false;
  }

  const last = blocks.at(-1);

  return last?.kind !== 'agent' || last.items.at(-1)?.kind === 'steps';
}

/**
 * The step the working line names while a turn runs (D13, contracts §5 step labels): the running turn's latest step
 * the table shows, as `stepLabel` reads it in the dashboard language with the turn's earlier calls (a read after
 * describing the menu is "Checking the menu…"). It names that step until the next one or the answer. Null before the
 * turn's first shown step (the line says "Working on it…"), once the turn is over, or when that step failed or was
 * denied (the step list shows it).
 */
export function platformAgentCurrentStep(conversation, language) {
  const messages = conversation?.messages ?? [];
  const last = messages.at(-1);

  if (last?.role !== 'assistant' || !isTurnActive(conversation, last)) {
    return null;
  }

  const turnId = last.metadata?.turnId;
  const turn = turnId
    ? messages.filter((message) => message.role === 'assistant' && message.metadata?.turnId === turnId)
    : [last];
  const calls = [];
  let current = null;

  turn.forEach((message) => {
    message.parts.forEach((part) => {
      if (part.type !== 'dynamic-tool') {
        return;
      }

      const label = stepLabel(part.toolName, part.input, language, calls);
      calls.push({ tool: part.toolName, input: part.input });

      if (label) {
        current = { id: part.toolCallId, label, state: stepState(part, true) };
      }
    });
  });

  return current && ['running', 'done'].includes(current.state) ? { id: current.id, label: current.label } : null;
}

/**
 * The blocks without the steps the working line stands for while it shows: the running ones and the one it names
 * (`currentStepId`), so no step shows twice. A step list, or the agent's block, left with nothing goes.
 */
export function withoutWorkingSteps(blocks, currentStepId = null) {
  const shown = (step) => step.state !== 'running' && step.id !== currentStepId;

  return blocks.flatMap((block) => {
    if (block.kind !== 'agent' || block.items.every((item) => item.kind !== 'steps' || item.steps.every(shown))) {
      return [block];
    }

    const items = block.items.flatMap((item) => {
      if (item.kind !== 'steps') {
        return [item];
      }

      const steps = item.steps.filter(shown);

      return steps.length > 0 ? [{ ...item, steps }] : [];
    });

    return items.length > 0 ? [{ ...block, items }] : [];
  });
}

/** The person's last message, which Try again sends again. */
export function lastUserText(blocks) {
  return blocks.findLast((block) => block.kind === 'user')?.text ?? null;
}

/**
 * The turn's ephemeral context (§5 `clientContext`): the page the person is on and its screen guide id, the
 * location chosen in the header (`{ id }`, or null for all locations and on a single location; platformAgentLocation)
 * and the dashboard language.
 */
export function platformAgentClientContext({ page, location = null, language }) {
  return { page, location, language };
}

/** Headers for the agent (§1): the person's on-behalf-of headers as shop gets them, and the language. */
export function platformAgentHeaders(apiHeaders, language) {
  const headers = {};

  [
    'x-on-behalf-of-company',
    'x-on-behalf-of-franchise',
    'x-on-behalf-of-service-provider',
    'x-preferred-locale',
  ].forEach((name) => {
    const value = apiHeaders?.[name];

    if (value !== undefined && value !== null && String(value) !== '') {
      headers[name] = String(value);
    }
  });

  if (language) {
    headers['accept-language'] = language;
  }

  return headers;
}

/** The SSO bearer from the API client's Authorization header, or null without a login. */
export function platformAgentBearer(apiHeaders) {
  const match = /^Bearer (.+)$/.exec(apiHeaders?.Authorization ?? '');

  return match && match[1] !== 'undefined' && match[1] !== 'null' ? match[1] : null;
}
